/*
 * The client's pages (draft 3), opened from a text or email with no sign in: the pay page (tips 10, 15, 20 percent or
 * Other with nothing picked to start, the card always saved and the page says so, pay by card through Stripe test mode) and
 * the quote page (pick add-ons, type your name to approve). The brand's color runs across the top and bottom.
 */
// What every client page shares: the brand frame, the account menu, the card logo, not found.
import type { PublicInvoice } from "@steward/shared";
import { useRouter } from "@tanstack/react-router";
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import { CardTermsLink } from "../../components/CardFields";
import { Icon } from "../../components/Icon";
import { prettyPhone } from "../../components/parts";

/**
 * My account (owner, 2026-11-01): a menu button on the right of the band, like a website's, opening the client's hub from
 * this pay or quote link (no code to look) at its home, visits or invoices, plus a call button.
 */
function AccountMenu({ account, phone }: { account: string; phone: string | null }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", key);
    };
  }, [open]);
  return (
    <div className="public-menu" ref={box}>
      <button type="button" className="public-menu-btn" aria-label="Menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? "close" : "menu"} size={24} />
      </button>
      {open ? (
        <nav className="public-menu-list" aria-label="My account">
          <a href={account}>
            <Icon name="person" size={20} />
            My account
          </a>
          <a href={`${account}#visits`}>
            <Icon name="schedule" size={20} />
            My visits
          </a>
          <a href={`${account}#invoices`}>
            <Icon name="invoices" size={20} />
            My invoices
          </a>
          {phone ? (
            <a href={`tel:${phone}`}>
              <Icon name="phone" size={20} />
              Call us
            </a>
          ) : null}
        </nav>
      ) : null}
    </div>
  );
}

/**
 * The old Steward's client frame (clientframe.php): the brand's white logo on its accent color on top, nothing to tap away
 * to, and the footer band in its footer color with its name, address, phone, links and the year. Always light.
 */
export function Branded({
  brand,
  children,
  wide,
  account,
  title,
}: {
  brand: PublicInvoice["brand"] | null;
  children: ReactNode;
  wide?: boolean;
  /** My account (owner, 2026-11-01): the client's hub, opened from this pay or quote link with no code. */
  account?: string | undefined;
  /** The browser tab's title (link-titles.ts); the brand's name without one, never "Steward" (owner, 2026-11-02). */
  title?: string | undefined;
}) {
  const tab = title ?? brand?.name;
  useEffect(() => {
    if (!tab) return;
    const was = document.title;
    document.title = tab;
    return () => {
      document.title = was;
    };
  }, [tab]);
  const look = brand?.look;
  const top = look?.accent ?? brand?.color ?? "#1f6f5c";
  const bottom = look?.menu ?? "#1d1d1f";
  const foot = useRef<HTMLElement>(null);
  // On a phone the brand runs into the bars (owner, 2026-11-01): the status bar and pulling past the top take the top band's
  // color, pulling past the end takes the footer's. iPhone Safari 26 skips theme-color and tints from the page color (html),
  // which is also what shows past either end, so html is the band's color until the footer is on screen, then the footer's.
  useEffect(() => {
    const metas = [...document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')];
    const was = metas.map((m) => m.content);
    for (const m of metas) m.content = top;
    const root = document.documentElement;
    const before = { html: root.style.backgroundColor, body: document.body.style.backgroundColor };
    document.body.style.backgroundColor = bottom;
    const paint = () => {
      const f = foot.current?.getBoundingClientRect();
      root.style.backgroundColor = window.scrollY > 0 && f && f.top < window.innerHeight ? bottom : top;
    };
    paint();
    window.addEventListener("scroll", paint, { passive: true });
    window.addEventListener("resize", paint);
    return () => {
      window.removeEventListener("scroll", paint);
      window.removeEventListener("resize", paint);
      metas.forEach((m, n) => (m.content = was[n] ?? m.content));
      root.style.backgroundColor = before.html;
      document.body.style.backgroundColor = before.body;
    };
  }, [top, bottom]);
  const style = {
    "--brand": top,
    "--brand-deep": look?.deep ?? brand?.color ?? "#1f6f5c",
    "--brand-menu": bottom,
    "--brand-cta": look?.cta ?? brand?.color ?? "#1f6f5c",
    "--brand-on-cta": look?.onCta ?? "#ffffff",
  } as CSSProperties;
  return (
    <div className="public" style={style}>
      <header className="public-top">
        {look?.logo ? <img src={look.logo} alt={brand?.name ?? ""} /> : brand ? <b>{brand.name}</b> : null}
        {account ? <AccountMenu account={account} phone={brand?.phone ?? null} /> : null}
      </header>
      <main className={`public-main${wide ? " wide" : ""}`}>
        {__PREVIEW__ ? <PreviewBack /> : null}
        {children}
      </main>
      {brand ? (
        <footer className="public-foot" ref={foot}>
          <b>{brand.name}</b>
          {brand.address ? <> · {brand.address}</> : null}
          {brand.phone ? (
            <>
              {" "}
              · <a href={`tel:${brand.phone.replace(/[^0-9+]/g, "")}`}>{prettyPhone(brand.phone)}</a>
            </>
          ) : null}
          {brand.links.length ? (
            <>
              <br />
              {brand.links.map((l, n) => (
                <span key={l.label}>
                  {n ? " · " : null}
                  <a href={l.url} target="_blank" rel="noopener noreferrer">
                    {l.label}
                  </a>
                </span>
              ))}
            </>
          ) : null}
          <br />
          <CardTermsLink /> · © {new Date().getFullYear()} {brand.name}
        </footer>
      ) : (
        <footer className="public-foot" />
      )}
    </div>
  );
}

/** The preview has no address bar, so the client pages get a way back to the app. */
function PreviewBack() {
  const router = useRouter();
  return (
    <button type="button" className="link small" onClick={() => router.history.back()}>
      Back to the app (preview only)
    </button>
  );
}

/** A card brand as people say it (Stripe sends visa, mastercard, amex). */
export function brandWord(brand: string): string {
  const b = brand.toLowerCase();
  return b === "amex" ? "Amex" : b === "mastercard" ? "Mastercard" : b.charAt(0).toUpperCase() + b.slice(1);
}

/** The card's own badge, the same ones as Cards we take; a brand we have none for shows nothing. */
export function CardLogo({ brand }: { brand: string }) {
  const b = brand.toLowerCase();
  if (b === "visa")
    return (
      <span className="cp-cb visa" aria-hidden="true">
        VISA
      </span>
    );
  if (b === "amex")
    return (
      <span className="cp-cb amex" aria-hidden="true">
        AMEX
      </span>
    );
  if (b === "discover")
    return (
      <span className="cp-cb dsc" aria-hidden="true">
        DISCOVER
      </span>
    );
  if (b === "mastercard")
    return (
      <span className="cp-cb mc" aria-hidden="true">
        <i />
        <i />
      </span>
    );
  return null;
}

export function NotFound({ what }: { what: string }) {
  return (
    <Branded brand={null}>
      <h1>This link does not work</h1>
      <p className="muted">The {what} may have been replaced. Ask us for a new link.</p>
    </Branded>
  );
}

// Pay page -------------------------------------------------------------------------------------------------------------
